import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  AfterViewInit,
  OnDestroy,
} from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { Store, select } from "@ngrx/store";
import { toSignal } from "@angular/core/rxjs-interop";
import { fromEvent, auditTime, Subscription } from "rxjs";
import { CoreSelectors } from "../../store/selectors";
import { CoreActions } from "../../store/actions";
import { MenuComponent } from "../menu/menu.component";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { BreadcrumbComponent } from "../breadcrumb/breadcrumb.component";
import { ThemeSwitcherComponent } from "../theme-switcher/theme-switcher.component";

interface ScreenState {
  desktop: boolean;
  tablet: boolean;
  mobile: boolean;
}
@Component({
  selector: "audio-layout",
  templateUrl: "./layout.component.html",
  styleUrls: ["./layout.component.sass"],
  imports: [
    MenuComponent,
    FaIconComponent,
    BreadcrumbComponent,
    RouterOutlet,
    ThemeSwitcherComponent,
  ],
  host: {
    "[class.menu-hidden]": "this.$menuHidden()",
    "[class.mobile]": "this.$mobile()",
    "[class.tablet]": "this.$tablet()",
    "[class.desktop]": "this.$desktop()",
  },

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements AfterViewInit, OnDestroy {
  private store = inject<Store>(Store);
  private router = inject(Router);
  $goToTop = signal(false);
  $windowScrolled = signal(false);

  @ViewChild("mainBody", { static: true }) mainBody!: ElementRef;

  $routerEvents = toSignal(this.router.events);

  $screen = toSignal(this.store.pipe(select(CoreSelectors.getScreen)), {
    initialValue: {
      desktop: false,
      tablet: false,
      mobile: false,
    } as ScreenState,
  });

  $menuHidden = signal(true);
  $desktop = computed(() => this.$screen().desktop);
  $tablet = computed(() => this.$screen().tablet);
  $mobile = computed(() => this.$screen().mobile);

  private scrollSubscription: Subscription | null = null;

  constructor() {
    effect(() => {
      if (this.$routerEvents() instanceof NavigationEnd) {
        this.closeMenu();
        const el = this.mainBody.nativeElement;
        if (typeof el.scrollTo === "function") {
          el.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          el.scrollTop = 0;
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.scrollSubscription = fromEvent(this.mainBody.nativeElement, "scroll")
      .pipe(auditTime(100))
      .subscribe(() => this.onScroll());
  }

  ngOnDestroy(): void {
    this.scrollSubscription?.unsubscribe();
  }

  onScroll(): void {
    const scrollTop = this.mainBody.nativeElement.scrollTop;

    if (scrollTop > 100) {
      this.$windowScrolled.set(true);
    } else if (scrollTop < 10) {
      this.$windowScrolled.set(false);
    }

    const position = scrollTop + this.mainBody.nativeElement.clientHeight / 2;
    this.store.dispatch(new CoreActions.SetScrollPosition(position));
  }

  toggleMenu(): void {
    if (this.$menuHidden()) {
      this.openMenu();
    } else {
      this.closeMenu();
    }
  }

  closeMenu(): void {
    this.$menuHidden.set(true);
  }

  openMenu(): void {
    this.$menuHidden.set(false);
  }

  async scrollToTop(): Promise<void> {
    this.$goToTop.set(true);
    const el = this.mainBody.nativeElement;
    if (typeof el.scrollTo === "function") {
      el.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      el.scrollTop = 0;
    }
    await new Promise((r) => setTimeout(r, 300));
    this.$goToTop.set(false);
    this.$windowScrolled.set(false);
  }
}
